import React, { useEffect, useState, useContext, useMemo } from "react";
import {
	Box,
	Modal,
	Card,
	CardContent,
	Grid,
	CircularProgress,
	Button,
	TextField,
	Autocomplete,
	Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { obtenerDatosPostDash, obtenerParams, auditPost } from "../../api/config";
// import { toPng } from "html-to-image";
// import saveAs from "file-saver";
import { GlobalContext } from "../../GlobalContext";
import Swal from "sweetalert2";
import { LineChartMUI, StackedBarChartMUI, TreemapChartRecharts } from "../Charts.jsx/ChartsMUI";

const getMesesNombres = (t) => [
	{ id: 1, nombre: t("aux.months.Jan") },
	{ id: 2, nombre: t("aux.months.Feb") },
	{ id: 3, nombre: t("aux.months.Mar") },
	{ id: 4, nombre: t("aux.months.Apr") },
	{ id: 5, nombre: t("aux.months.May") },
	{ id: 6, nombre: t("aux.months.Jun") },
	{ id: 7, nombre: t("aux.months.Jul") },
	{ id: 8, nombre: t("aux.months.Aug") },
	{ id: 9, nombre: t("aux.months.Sep") },
	{ id: 10, nombre: t("aux.months.Oct") },
	{ id: 11, nombre: t("aux.months.Nov") },
	{ id: 12, nombre: t("aux.months.Dec") },
];
const ModalChart = ({ open, onClose, initialData }) => {
	const { t } = useTranslation();
	const { isGranted, getModuleId, site_id } = useContext(GlobalContext);
	const [isLoading, setIsLoading] = useState(false);
	const [dataComplete, setDataComplete] = useState(null);
	const [isDownloading, setIsDownloading] = useState(false);
	const [periodos, setPeriodos] = useState([]);
	const [selectedPeriodos, setSelectedPeriodos] = useState([]);

	const mesesNombres = useMemo(() => getMesesNombres(t), [t]);

	useEffect(() => {
		const fetchData = async () => {
			setIsLoading(true);
			try {
				const data = await obtenerDatosPostDash(site_id, [initialData]);

				setDataComplete(data);

				const allKeys = new Set();

				data.forEach((sim) => {
					Object.keys(sim).forEach((key) => {
						if (key !== "simulacion_id") allKeys.add(key);
					});
				});

				const periodosArr = Array.from(allKeys)
					.map((key) => {
						const [yearStr, monthStr] = key.split("-");
						const year = Number(yearStr);
						const month = Number(monthStr);
						const nombre = mesesNombres.find((m) => m.id === month)?.nombre || monthStr;
						return { id: key, year, month, label: `${nombre} ${year}` };
					})
					.sort((a, b) => (a.year !== b.year ? a.year - b.year : a.month - b.month));

				setPeriodos(periodosArr);
				// 3) Por defecto, seleccionamos todos los periodos
				setSelectedPeriodos(periodosArr);
			} catch (error) {
				console.error("Error fetching data:", error);
			}
			setIsLoading(false);
		};

		if (open) {
			fetchData();
		}
	}, [open, initialData, site_id, mesesNombres]);

	// const handleDownloadCharts = async () => {
	// 	setIsDownloading(true);

	// 	try {
	// 		const container = document.getElementById("charts-container");
	// 		const dataUrl = await toPng(container, { quality: 1 });
	// 		const blob = dataURItoBlob(dataUrl);
	// 		saveAs(blob, "charts.png");
	// 		// envio de auditoria
	// 		const moduleId = getModuleId("Simulación", "Descargar resultados (gráficos)");
	// 		auditPost(moduleId, site_id);
	// 	} catch (error) {
	// 		console.error("Error al generar la imagen PNG:", error);
	// 		// Aquí podrías agregar código para manejar el error, como mostrar un mensaje al usuario
	// 	} finally {
	// 		setIsDownloading(false);
	// 	}
	// };

	// const dataURItoBlob = (dataURI) => {
	// 	const byteString = atob(dataURI.split(",")[1]);
	// 	const mimeString = dataURI.split(",")[0].split(":")[1].split(";")[0];
	// 	const ab = new ArrayBuffer(byteString.length);
	// 	const ia = new Uint8Array(ab);
	// 	for (let i = 0; i < byteString.length; i++) {
	// 		ia[i] = byteString.charCodeAt(i);
	// 	}
	// 	return new Blob([ab], { type: mimeString });
	// };

	const handleDownloadData = async () => {
		setIsDownloading(true);

		try {
			const datosEjecucion = await obtenerParams(
				`/outputs/executions_results_tables/${initialData}?site_id=${site_id}`,
				null
			);
			const url = window.URL.createObjectURL(new Blob([datosEjecucion]), {
				type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
			});
			const link = document.createElement("a");
			link.href = url;
			// eslint-disable-next-line no-useless-concat
			link.setAttribute("download", "Resultados" + ".xlsx");
			document.body.appendChild(link);
			link.click();
			document.body.removeChild(link);
			const moduleId = getModuleId("Simulación", "Descarga resultados (tablas)");
			auditPost(moduleId, site_id);
		} catch (error) {
			console.error("Error al obtener datos:", error);
			Swal.fire({
				title: t("aux.messages.alert.error"),
				text: t("aux.messages.alert.text_alert.error_download"),
				icon: "error",
			});
		} finally {
			setIsDownloading(false);
		}
	};

	return (
		<>
			<Modal
				open={open}
				onClose={onClose}
				sx={{
					display: "flex",
					alignItems: "center",
					justifyContent: "center",
				}}
			>
				{isLoading ? ( // Mostrar la pantalla de carga si isLoading es true
					<Box
						sx={{ mt: 4, width: "100%", display: "flex", flexDirection: "column", alignItems: "center" }}
					>
						<CircularProgress color="primary" />
					</Box>
				) : (
					<Box
						sx={{
							width: "80%",
							height: "90%",
							bgcolor: "secondary.main",
							borderRadius: "10px",
							p: 4,
							display: "flex",
							justifyContent: "space-between",
							flexDirection: "column",
							lignItems: "center",
							overflowY: "auto",
						}}
					>
						<Grid container spacing={0.5} alignItems="center">
							<Grid item xs={12}>
								<Autocomplete
									multiple
									size="small"
									options={periodos}
									value={selectedPeriodos}
									onChange={(e, v) => setSelectedPeriodos(v)}
									getOptionLabel={(opt) => opt.label}
									isOptionEqualToValue={(opt, val) => opt.id === val.id}
									renderInput={(params) => (
										<TextField
											{...params}
											label={t("aux.filter_per_month")}
											variant="outlined"
											sx={{ "& .MuiInputBase-root": { padding: "4px", height: "36px" } }}
										/>
									)}
								/>
							</Grid>
						</Grid>
						{dataComplete && dataComplete.length > 0 && (
							<Box sx={{ mt: 1 }} id="charts-container">
								<Grid container spacing={1}>
									{dataComplete.map((sim, index) => (
										<Grid item xs={12} key={index}>
											<Box sx={{ mb: 2 }}>
												<Typography variant="h6" sx={{ textAlign: "center" }}>
													{t("charts.simulation")} {sim.simulacion_id}
												</Typography>
												{/* Todos los gráficos agrupados en una sola Card */}
												<Card sx={{ mb: 1 }}>
													<CardContent>
														<Grid container spacing={1}>
															{/* Gráfico de líneas */}
															<Grid item xs={12}>
																{sim.error ? (
																	<Typography sx={{ textAlign: "center" }} variant="subtitle1">
																		{t("aux.no_data")}
																	</Typography>
																) : (
																	<LineChartMUI data={sim} selectedPeriods={selectedPeriodos} />
																)}
															</Grid>

															{/* Gráfico de barras apiladas */}
															<Grid item xs={12}>
																{sim.error ? (
																	<Typography sx={{ textAlign: "center" }} variant="subtitle1">
																		{t("aux.no_data")}
																	</Typography>
																) : (
																	<StackedBarChartMUI data={sim} selectedPeriods={selectedPeriodos} />
																)}
															</Grid>
															{/* Treemap */}
															<Grid item xs={12}>
																{sim.error ? (
																	<Typography sx={{ textAlign: "center" }} variant="subtitle1">
																		{t("aux.no_data")}
																	</Typography>
																) : (
																	<TreemapChartRecharts data={sim} selectedPeriods={selectedPeriodos} />
																)}
															</Grid>
														</Grid>
													</CardContent>
												</Card>
											</Box>
										</Grid>
									))}
								</Grid>
							</Box>
						)}

						<Box sx={{ mt: 2 }}>
							<Grid container spacing={3}>
								{/* <Grid item xs={6}>
									<Button
										sx={{ width: "100%" }}
										variant="contained"
										color="primary"
										disabled={isDownloading || !isGranted("Simulación", "Descargar resultados (gráficos)")}
										onClick={handleDownloadCharts}
									>
										{isDownloading ? t("aux.downloading") : t("aux.buttons.chart.download_chart")}
									</Button> 
								</Grid>*/}
								<Grid item xs={12}>
									<Button
										sx={{ width: "100%" }}
										variant="contained"
										color="primary"
										disabled={isDownloading || !isGranted("Simulación", "Descarga resultados (tablas)")}
										onClick={handleDownloadData}
									>
										{isDownloading ? t("aux.downloading") : t("aux.buttons.chart.download_grid")}
									</Button>
								</Grid>
							</Grid>
						</Box>
					</Box>
				)}
			</Modal>
		</>
	);
};

export { ModalChart };
